<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import unitService from '@/services/unit.service'
import UnitDrawer from '@/views/masters/unit/drawer/UnitDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    name: 'master-unit',
    subject: 'global.menu',
    action: 'master_unit',
  },
})

const snackbar = useToast()

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  display_name: [],
  status: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: 'Code', key: 'code' },
  { title: 'Name', key: 'name' },
  { title: 'Display Name', key: 'display_name' },
  { title: 'Status', key: 'status' },
  { title: 'Actions', key: 'actions', sortable: false },
]

const {
  data: unitsData,
  isFetching: isFetchingUnits,
  execute: fetchUnits,
} = await useApi(createUrl('/masters/units', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const units = computed(() => unitsData.value?.data)
const totalUnits = computed(() => unitsData.value?.meta?.total)

const updateUnit = data => {
  if (data.id) {
    var isUpdated = false
    Object.keys(data).forEach(key => {
      if (unitDrawerData.value[key] !== data[key]) {
        isUpdated = true
      }
    })
  }

  if (!isUpdated) {
    isUnitDrawerVisible.value = false

    return
  }
  unitService.updateUnit(data.id, data, () => {
    fetchUnits()
    isUnitDrawerVisible.value = false
  }, onError)
}

const saveUnit = data => {
  unitService.addUnit(data, () => {
    fetchUnits()
    isUnitDrawerVisible.value = false
  }, onError)
}

const isUnitDrawerVisible = ref(false)
const unitDrawerData = ref()
const unitDeleteData = ref()
const unitDrawerType = ref('Add')

const showUnit = (unitData, type) => {
  unitDrawerData.value = unitData
  unitDrawerType.value = type
  isUnitDrawerVisible.value = true
}

const deleteUnit = async (confirm, data) => {
  if (data?.id && confirm) {
    await unitService.deleteUnit(data.id, () => {
      fetchUnits()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = unitData => {
  unitDeleteData.value = unitData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) {
    unitDeleteData.value = {}
  }
})

// ===== Import Unit Functionality =====
const refImportUnitForm = ref()
const importUnitFile = ref()
const importUnitErrors = ref()
const isImportUnitLoading = ref(false)
const isImportUnitModal = ref(false)

const submitImportUnit = async () => {
  if (!importUnitFile.value) {
    importUnitErrors.value = { file: ["Please select a file to import."] }

    return
  }
  isImportUnitLoading.value = true
  importUnitErrors.value = null

  try {
    await unitService.import(
      {
        file: importUnitFile.value,
      },
      () => {
        snackbar.success("Units imported successfully.")
        isImportUnitModal.value = false
        fetchUnits()
        importUnitFile.value = null
      },
      error => {
        importUnitErrors.value = error.data?.errors || error._data?.message || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
      },
    )
  } catch (e) {
    console.error("Error during unit import submission:", e)
    snackbar.error(
      "An unexpected error occurred while submitting the import request.",
    )
    if (!importUnitErrors.value)
      importUnitErrors.value = { message: "Import submission failed." }
  } finally {
    isImportUnitLoading.value = false
  }
}

const downloadSampleUnitFile = async () => {
  await downloadFile(
    "masters/units/sample-import-file/export",
    "unit-sample-excel.xlsx",
    () => {},
    () => {},
  )
}

watch(
  () => isImportUnitModal.value,
  value => {
    if (value === false) {
      importUnitFile.value = null
      importUnitErrors.value = null
    }
  },
)
// ===== End Import Unit Functionality =====
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div style="inline-size: 10rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            v-if="$can('import', 'master.unit')"
            variant="flat"
            prepend-icon="tabler-download"
            @click="isImportUnitModal = !isImportUnitModal"
          >
            Import Units
          </VBtn>

          <VBtn
            v-if="$can('create', 'master.unit')"
            variant="flat"
            prepend-icon="tabler-plus"
            @click="showUnit({}, 'Add')"
          >
            Add New Unit
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="units"
        :items-length="totalUnits"
        :headers="headers"
        :loading="isFetchingUnits"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="cursor-pointer text-base font-weight-medium"
            @click="showUnit(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code ?? '—' }}</span>
        </template>

        <template #item.display_name="{ item }">
          <span class="text-medium-emphasis">{{ item.display_name ?? '—' }}</span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status ? 'success' : 'error'"
            size="small"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.unit')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.unit')"
            @click="showUnit(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <UnitDrawer
      v-model:isDrawerOpen="isUnitDrawerVisible"
      :unit-data="unitDrawerData"
      :type="unitDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="unitDrawerType = $event"
      @update:unit-data="updateUnit"
      @save-unit="saveUnit"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Unit Deleted."
      confirm-title="Deleted!"
      :data="unitDeleteData"
      @confirm="deleteUnit"
    />

    <!-- Import Unit Modal -->
    <VDialog
      v-model="isImportUnitModal"
      max-width="600"
      :persistent="isImportUnitLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportUnitLoading"
        @click="isImportUnitModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Units
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportUnitForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportUnitLoading"
              :error-messages="importUnitErrors?.file"
              @change="
                (e) => {
                  importUnitFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText v-if="importUnitErrors?.message && !Array.isArray(importUnitErrors)">
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importUnitErrors.message = null"
          >
            {{ importUnitErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors -->
        <VCardText v-if="Array.isArray(importUnitErrors) && importUnitErrors.length > 0">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-error text-caption font-weight-medium">
              {{ importUnitErrors.length }}
              {{ importUnitErrors.length === 1 ? 'Error' : 'Errors' }} found
            </div>
            <VBtn
              icon
              color="error"
              variant="text"
              size="small"
              class="ma-0 pa-0"
              density="compact"
              @click="importUnitErrors = []"
            >
              <VIcon
                color="error"
                size="18"
                icon="tabler-x"
              />
            </VBtn>
          </div>
          <div
            class="d-flex flex-column gap-2 scrollable-errors"
            style="max-height: 90px; overflow-y: auto;"
          >
            <VAlert
              v-for="(error, index) in importUnitErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
              style="flex-shrink: 0;"
            >
              {{ error.row ? `Row ${error.row}: ` : "" }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportUnitLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="flat"
            color="primary"
            :disabled="isImportUnitLoading"
            @click="downloadSampleUnitFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="flat"
            color="error"
            :disabled="isImportUnitLoading"
            @click="isImportUnitModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="flat"
            :loading="isImportUnitLoading"
            :disabled="isImportUnitLoading || !importUnitFile"
            @click="submitImportUnit"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style lang="css">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}

/* For Chrome, Edge, Safari */
.scrollable-errors::-webkit-scrollbar {
  width: 2px;
}

.scrollable-errors::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 2px;
}

.scrollable-errors::-webkit-scrollbar-track {
  background-color: transparent;
}

/* For Firefox */
.scrollable-errors {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}
</style>
